<mat-toolbar color="primary" class="walk-header">
    <img [src]=IMAGE_LINQ alt="LINQ" width="80" height="55">
    <span class="walk-title">Learn the basics of modelling</span>
    <span class="spacer"></span>
    @if (stage !== STAGE.WELCOME) {
        <button mat-button (click)="restart()" matTooltip="Clear the canvas and start the walkthrough again"><mat-icon>replay</mat-icon>Start again</button>
    }
    <button mat-button (click)="return()" matTooltip="Leave the walkthrough. Nothing here is saved to your organisation"><mat-icon>logout</mat-icon>Exit</button>
</mat-toolbar>

@if (tutorialGraph && linqset && stage !== STAGE.WELCOME) {
    <app-graph-toolbar [organisation]="organisation" [screen]="screen" [linqset]="linqset" [modelType]="modelType" [readOnly]="false" [graphComponent]="tutorialGraph.graphComponent" [functions]="menuFunctions" [highlights]="stage === STAGE.LAYOUT ? [FUNCTION.RESET_LAYOUT] : []" (toolbarEvent)="handleFunctionEvent($event)"></app-graph-toolbar>
}
@if (stage === STAGE.BUILD || stage === STAGE.PEOPLE) {
    <app-onboarding-palette [linqset]="linqset" [start]="index" [end]="endIndex" [canSelect]="canSelectNew"></app-onboarding-palette>
}

<mat-sidenav-container class="drawer-container" autosize>
    <mat-sidenav-content>
        <div class="canvas-area" [style.--action]="MODEL_COLOURS.ACTION_COLOR" [style.--information]="MODEL_COLOURS.INFO_COLOR" [style.--role]="MODEL_COLOURS.ROLE_COLOR" [style.--system]="MODEL_COLOURS.SYSTEM_COLOR">
            <div class="graph-component-tutorial">
                <app-tutorial-graph-component [linqset]="linqset" (itemCreatedEvent)="handleItemCreated($event)"></app-tutorial-graph-component>
            </div>

            @if (stage === STAGE.WELCOME) {
                <div class="welcome-backdrop">
                    <section class="welcome" aria-labelledby="welcome-title">
                        <h1 id="welcome-title">Learn to model in a few minutes</h1>
                        <p class="lead">You'll build a model of a simple recruitment process, posting a job ad, and see what LINQ shows about it. Nothing here is saved to your organisation, so you can't break anything.</p>
                        <ol class="welcome-parts">
                            <li><span class="part-number">1</span><div><b>Build the main flow</b><span>Drag the steps onto the canvas and connect them.</span></div></li>
                            <li><span class="part-number">2</span><div><b>Tidy it up</b><span>Let LINQ lay the model out for you.</span></div></li>
                            <li><span class="part-number">3</span><div><b>Add the people and systems</b><span>Connect who does the work, and the tools they use.</span></div></li>
                            <li><span class="part-number">4</span><div><b>See the insights</b><span>The time and cost of the process, worked out from your model.</span></div></li>
                        </ol>
                        <div class="welcome-actions">
                            <button mat-button (click)="notNow()" matTooltip="You can do the walkthrough any time from the help menu">Not now</button>
                            <button mat-flat-button color="accent" (click)="start()" data-testid="walkthrough-start">Start the walkthrough<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
                        </div>
                    </section>
                </div>
            }@else {
                <section class="guide" aria-labelledby="guide-title">
                    <div class="guide-eyebrow">{{stage === STAGE.DONE ? 'Finished' : 'Step ' + step + ' of ' + totalSteps}}</div>
                    <ol class="parts" [attr.aria-label]="'Part ' + (partIndex + 1) + ' of ' + parts.length">
                        @for (p of parts; track p.stage; let i = $index) {
                            <li [class.done]="i < partIndex" [class.current]="i === partIndex" [attr.aria-current]="i === partIndex ? 'step' : null">
                                <span class="bar"></span><span class="label">{{p.label}}</span>
                            </li>
                        }
                    </ol>
                    <h2 id="guide-title">{{guide.title}}</h2>
                    <div class="guide-text" aria-live="polite" [innerHTML]="guide.html"></div>

                    @if (guide.how === 'drag') {
                        <p class="how"><mat-icon fontSet="material-symbols-outlined" aria-hidden="true">drag_pan</mat-icon><span>Press and hold the highlighted node in the strip above, move it onto the canvas, then let go.</span></p>
                    }@else if (guide.how === 'connect') {
                        <p class="how"><mat-icon aria-hidden="true">trending_flat</mat-icon><span>Edges start from the middle of a highlighted node. If the blue dot doesn't appear, move the pointer slowly over the node's centre.</span></p>
                    }

                    @if (stage === STAGE.LAYOUT) {
                        <div class="guide-actions">
                            <button mat-flat-button color="accent" (click)="tidyLayout()" data-testid="walkthrough-tidy"><mat-icon>device_hub</mat-icon>Tidy the layout</button>
                        </div>
                    }@else if (stage === STAGE.DONE) {
                        <div class="guide-actions">
                            <button mat-flat-button color="accent" (click)="seeInsights()">See the insights</button>
                            <button mat-stroked-button (click)="restart()">Start again</button>
                            <button mat-button (click)="return()">Exit</button>
                        </div>
                    }
                </section>
            }
        </div>
    </mat-sidenav-content>
    <mat-sidenav #insightsdrawer mode="side" [opened]="false" position="end" class="insights-drawer">
        @if (linqset) {
            <mat-tab-group class="insights-tabs" [(selectedIndex)]="insightsTab">
                <mat-tab label="Summary">
                    <ul class="summary">
                        <li>The business posts <b>5</b> job ads each work day for 48 weeks of the year: <b>1,200</b> a year.</li>
                        <li>There are <b>5</b>&nbsp;<span class="kind kind-role">Recruitment admin</span> staff, who work <b>8</b> hours each work day, <b>48</b> weeks a year. Each costs <b>$70,000</b> a year on average.</li>
                        <li>There is <b>1</b>&nbsp;<span class="kind kind-role">Recruitment lead</span>, who works the same hours and costs <b>$100,000</b> a year.</li>
                        <li><span class="kind kind-system">MS Outlook</span>&nbsp;and&nbsp;<span class="kind kind-system">MS Word</span> cost <b>$100</b> per person a year. The job website costs <b>$20,000</b> a year, and the intranet's board <b>$5,000</b>.</li>
                        <li>Each <span class="kind kind-action">action</span> has a time to process one job ad, which gives the time the whole process takes.</li>
                        <li>See the <b>Overview</b> and <b>Nodes</b> tabs for the model's insights.</li>
                    </ul>
                </mat-tab>
                <mat-tab label="Overview">
                    @defer {
                        <app-insights-overview class="tab-body" [linqset]="linqset" [graph]="tutorialGraph.graphComponent.graph" [trigger]="trigger"></app-insights-overview>
                    }
                </mat-tab>
                <mat-tab label="Nodes">
                    @defer {
                        <app-node-table [linqset]="linqset" [trigger]="trigger" [organisationLinqset]="organisationLinqset" [graphComponent]="tutorialGraph.graphComponent"></app-node-table>
                    }
                </mat-tab>
            </mat-tab-group>
        }
    </mat-sidenav>
</mat-sidenav-container>
